import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { SPECIALTIES, INSURANCES } from "@/data/heallexa";
import { SEO_CITIES, specialtyOverviewPath } from "@/lib/seo-slugs";

const SITE = "https://heallexa.com";
const PROVIDERS_PER_DIRECTORY_PAGE = 1000;

export const Route = createFileRoute("/sitemap")({
  head: () => ({
    meta: [
      { title: "Heallexa Sitemap: Browse All Doctors, Cities & Specialties" },
      {
        name: "description",
        content:
          "Browse Heallexa's full directory: doctor profiles, specialties, cities, states, insurance carriers, and conditions across the United States, all in one place.",
      },
      { property: "og:title", content: "Heallexa Sitemap: Browse All Doctors, Cities & Specialties" },
      {
        property: "og:description",
        content: "Explore every Heallexa directory page: doctor profiles, specialties, cities, states, insurance carriers, and conditions covered nationwide.",
      },
    ],
    links: [{ rel: "canonical", href: `${SITE}/sitemap` }],
  }),
  errorComponent: () => (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-16">
        <h1 className="text-3xl font-semibold mb-3">Sitemap</h1>
        <p className="text-muted-foreground">
          We hit a snag loading the live counts. Try{" "}
          <a href="/sitemap.xml" className="text-primary hover:underline">/sitemap.xml</a> or{" "}
          <a href="/doctors/directory" className="text-primary hover:underline">browse the doctor directory</a>.
        </p>
      </main>
      <FooterSection />
    </div>
  ),
  component: SitemapHub,
});

function SitemapHub() {
  const previewPages = 12;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-16">
        <header className="mb-12">
          <p className="text-sm font-medium text-primary mb-2">Heallexa Directory</p>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
            Sitemap
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl">
            A complete index of Heallexa: every provider profile, specialty, city, state and
            insurance carrier we cover.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <Section title="Doctor Profiles" href="/doctors/directory">
            <p className="text-sm text-muted-foreground mb-4">
              Healthcare providers across directory pages of {PROVIDERS_PER_DIRECTORY_PAGE.toLocaleString()} profiles each.
            </p>
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: previewPages }).map((_, i) => (
                <a
                  key={i}
                  href={`/doctors/directory/${i}`}
                  className="px-3 py-1.5 rounded-md bg-card border border-border text-sm hover:bg-accent transition"
                >
                  {i}
                </a>
              ))}
              <a
                href="/doctors/directory"
                className="px-3 py-1.5 rounded-md bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition"
              >
                View all pages →
              </a>
            </div>
          </Section>

          <Section title="Landing Pages" href="/landing-pages/directory">
            <p className="text-sm text-muted-foreground">
              Specialty, city, state and insurance landing pages generated from our provider
              data. Coming soon.
            </p>
          </Section>

          <Section title="Top Specialties" href="/specialties">
            <ul className="grid grid-cols-2 gap-y-2 text-sm">
              {SPECIALTIES.slice(0, 16).map((s) => (
                <li key={s}>
                  <a
                    href={specialtyOverviewPath(s)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {s}
                  </a>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Top Cities" href="/locations">
            <ul className="grid grid-cols-2 gap-y-2 text-sm">
              {SEO_CITIES.slice(0, 16).map((c) => (
                <li key={c}>
                  <a
                    href={`/search?location=${encodeURIComponent(c)}`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {c}
                  </a>
                </li>
              ))}
            </ul>
          </Section>


          <Section title="Insurance Carriers" href="/insurance" hideArrow>
            <ul className="grid grid-cols-2 gap-y-2 text-sm">
              {INSURANCES.slice(0, 12).map((i) => (
                <li key={i.id}>
                  <Link
                    to="/insurance/$slug"
                    params={{ slug: i.id }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {i.name}
                  </Link>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Browse All Specialties" href="/specialties">
            <p className="text-sm text-muted-foreground">
              {SPECIALTIES.length}+ medical, dental, mental health and wellness specialties.
            </p>
            <Link to="/specialties" className="inline-block mt-3 text-sm text-primary hover:underline">
              See every specialty →
            </Link>
          </Section>

        </div>

        <div className="mt-16 pt-8 border-t border-border text-sm text-muted-foreground">
          Looking for the XML sitemap?{" "}
          <a href="/sitemap.xml" className="text-primary hover:underline">
            /sitemap.xml
          </a>
        </div>
      </main>
      <FooterSection />
    </div>
  );
}

function Section({
  title,
  href,
  children,
  hideArrow,
}: {
  title: string;
  href: string;
  children: React.ReactNode;
  hideArrow?: boolean;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <h2 className="text-xl font-semibold text-foreground mb-1">
        <a href={href} className="hover:text-primary transition">
          {title} {!hideArrow ? <span className="text-muted-foreground">→</span> : null}
        </a>
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}
